/**
 * Type representing a range of dates.
 */
export type RangeDate = {
  from: Date[];
  to: Date[];
};

/**
 * Type representing date-related properties for a table.
 */
export type TableDate = {
  /**
   * An array of available years.
   */
  availableYears: number[];

  /**
   * The selected range of dates.
   */
  selectedDate: RangeDate;

  /**
   * The initial range of dates.
   */
  initialDate: RangeDate;

  /**
   * The limit date for the range picker.
   */
  dateLimits?: [Date, Date];
};

/**
 * Enum representing different tables.
 */
export enum Tables {
  CII_FLEET_RATINGS = 'CII_FLEET_RATINGS',
  EEOI_SCORES = 'EEOI_SCORES',
}

/**
 * Type representing the state of the date picker.
 */
export type DatePickerState = {
  /**
   * Date-related properties for the EEOI_SCORES table.
   */
  [Tables.EEOI_SCORES]: TableDate;

  /**
   * Year-related properties for the CII_FLEET_RATINGS table.
   */
  [Tables.CII_FLEET_RATINGS]: TableDate;
};

/**
 * Type representing actions for the date picker.
 */
export type DatePickerActions = {
  /**
   * Function to set the date or year for a specific table.
   * @param rangeDate The range of dates or year to set.
   * @param table The table for which to set the date or year.
   */
  setDate: (rangeDate: RangeDate, table: keyof typeof Tables) => void;

  /**
   * Function to set the available years for a specific table.
   * @param availableYears The array of available years.
   * @param table The table for which to set the available years.
   */
  setAvailableYears: (availableYears: number[], table: keyof typeof Tables) => void;

  /**
   *  Function to set the date limits for a specific table.
   * @param dateLimits An array of date limits to set for a specific table.
   * @param table The table for which to set the
   */
  setDateLimits: (dateLimits: [Date, Date], table: keyof typeof Tables) => void;
};

/**
 * Type representing the state and actions of the date picker.
 */
export type DatePickerStore = DatePickerState & DatePickerActions;
